import { useQuery } from "@tanstack/react-query";
import { API_ROUTES } from "../../constants";
import { apiClient } from "./apiClient";
import { QUERY_KEYS } from "./constants";
import type { ChatMessage, MessageNode } from "./types";

/**
 * Transforms a MessageNode (API response) to ChatMessage (UI representation)
 */
export const transformMessageNodeToChatMessage = (
  node: MessageNode
): ChatMessage => ({
  id: node.id,
  role: node.message.role || "assistant",
  content: node.message.content || "",
  createdAt: node.date_time,
  tool_calls: node.message.tool_calls,
});

/**
 * Hook to fetch chat messages for an existing chat session
 */
export const useGetSession = (sessionId: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.getSession(sessionId),
    queryFn: async () => {
      if (!sessionId) {
        throw new Error("Session ID is required");
      }

      const response = await apiClient.get<MessageNode[]>(
        API_ROUTES.omniSearch.chat(sessionId)
      );

      return response.data;
    },
    select: (data) => {
      // Normalize the data to ensure all messages have content.
      const normalizedData = data.filter((item) => {
        return item.message.content;
      });

      // Find the index of the first user message item in the session.
      const firstUserMessageIndex = normalizedData.findIndex(
        (item) => item.message.role === "user"
      );
      // If there are no user messages, return an empty array.
      if (firstUserMessageIndex === -1) return [];

      // Extract messages starting from the first user message and transform them
      return normalizedData
        .slice(firstUserMessageIndex)
        .map(transformMessageNodeToChatMessage);
    },
    enabled: !!sessionId,
  });
};
